import React from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { PrimeIcons } from 'primereact/api';
import { classNames } from 'primereact/utils';
import { ExerciseDay } from '../../../types/Fisiofit/routine';
import { ExerciseItem } from '../exercises/ExerciseItem';
import { DayItemSharedProps } from '../types';
import { ExerciseDragClone } from './ExerciseDragClone';

type DayItemProps = DayItemSharedProps & {
  day: ExerciseDay;
  dragHandleProps?: any;
};

// One day card: header with the name editor and the day actions, then the exercise list (draggable while editing)
export const DayItem = ({ day, dragHandleProps, days, exerciseHeaderRef, exerciseListWidth, ...rowProps }: DayItemProps) => {
  const { form, isEditing, isCreating, validationErrors, exercises } = rowProps;
  const hasError = day.exercises.length === 0 && !!validationErrors?.exerciseDays;
  const dayIndex = form.exerciseDays.findIndex((d) => d.dayNumber === day.dayNumber);

  // Si el índice no se encuentra, usar el número de día como referencia aproximada
  const calculatedDayIndex = dayIndex >= 0 ? dayIndex : day.dayNumber - 1;

  const isEditingThisDay = days.editingDayId === `day-${day.dayNumber}`;

  return (
    <div className={classNames('exercise-day-item', { 'p-invalid': hasError })}>
      <div className='exercise-day-header'>
        <div className='day-info'>
          {(isEditing || isCreating) && (
            <div className='drag-handle-day' {...dragHandleProps}>
              <i className='pi pi-bars' />
            </div>
          )}
          {isEditingThisDay ? (
            <InputText
              value={days.editingDayName}
              onChange={(e) => days.handleDayNameChange(e.target.value)}
              onBlur={(e) => days.handleFinishEditingDayName(calculatedDayIndex, e)}
              onKeyDown={(e) => days.handleDayNameKeyDown(calculatedDayIndex, e)}
              autoFocus
              className='day-name-edit'
            />
          ) : (
            <span className='day-number'>{day.name || `Día ${day.dayNumber}`}</span>
          )}
          <span className='exercise-count'>
            {day.exercises.length} ejercicio{day.exercises.length !== 1 ? 's' : ''}
          </span>
        </div>
        {(isEditing || isCreating) && (
          <div className='day-actions'>
            <Button
              icon='pi pi-plus'
              className='small-button'
              rounded
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                exercises.addExerciseToDay(calculatedDayIndex);
              }}
              text
              tooltip='Añadir ejercicio'
              tooltipOptions={{ position: 'top' }}
            />
            <Button
              icon='pi pi-pencil'
              className='small-button'
              rounded
              onClick={(e) => days.handleStartEditingDayName(day, calculatedDayIndex, e)}
              text
              tooltip='Editar nombre del día'
              tooltipOptions={{ position: 'top' }}
            />
            <Button
              icon='pi pi-trash'
              rounded
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                days.handleRemoveDay(day.dayNumber);
              }}
              text
              className='p-button-danger small-button'
              tooltip='Eliminar día'
              tooltipOptions={{ position: 'top' }}
            />
          </div>
        )}
      </div>
      <div className='exercise-day-content' ref={exerciseHeaderRef}>
        <Droppable
          droppableId={`day-${calculatedDayIndex}`}
          type='exercises'
          direction='vertical'
          renderClone={(provided, _snapshot, rubric) => (
            <ExerciseDragClone
              exercise={day.exercises[rubric.source.index]}
              provided={provided}
              exerciseListWidth={exerciseListWidth}
            />
          )}
        >
          {(provided, snapshot) => (
            <div
              className={classNames('exercise-list', 'exercise-dropzone', { 'is-dragging-over': snapshot.isDraggingOver })}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {day.exercises.length > 0 ? (
                day.exercises.map((exercise, exerciseIndex) => (
                  <Draggable
                    key={`exercise-${day.dayNumber}-${exerciseIndex}`}
                    draggableId={`exercise-${day.dayNumber}-${exerciseIndex}`}
                    index={exerciseIndex}
                  >
                    {(provided, snapshot) => (
                      <ExerciseItem
                        {...rowProps}
                        exercise={exercise}
                        dayIndex={calculatedDayIndex}
                        exerciseIndex={exerciseIndex}
                        provided={provided}
                        snapshot={snapshot}
                      />
                    )}
                  </Draggable>
                ))
              ) : (
                <div className='no-exercises error'>
                  <i className={PrimeIcons.EXCLAMATION_CIRCLE} />
                  <span>Debes añadir al menos un ejercicio</span>
                  <Button
                    label='Añadir ejercicio'
                    icon={PrimeIcons.PLUS}
                    className='p-button-sm'
                    onClick={() => exercises.addExerciseToDay(calculatedDayIndex)}
                  />
                </div>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </div>
  );
};
